/**
 * Fonte única de TipoNota para a V2 (Notas, Cartões, BI Financeiro, BI Veículo).
 * Não importar em views V1.
 */

export const TIPO_NOTA = {
  ABASTECIMENTO: 'ABASTECIMENTO',
  ALIMENTACAO: 'ALIMENTACAO',
  COMPRA_MANUTENCAO_VEICULAR: 'COMPRA_MANUTENCAO_VEICULAR',
  CORREIO: 'CORREIO',
  HOSPEDAGEM: 'HOSPEDAGEM',
  LIMPEZA_VEICULAR: 'LIMPEZA_VEICULAR',
  MANUTENCAO_VEICULO: 'MANUTENCAO_VEICULO',
  OUTROS: 'OUTROS',
  PEDAGIO: 'PEDAGIO',
};

/** Tipos removidos do cadastro/filtro; ainda usados só para label de notas legadas. */
export const TIPO_NOTA_LEGACY_LABELS = {
  ADIANTAMENTO: 'Adiantamento',
};

/**
 * @typedef {Object} TipoNotaDef
 * @property {string} enum
 * @property {string} kpiKey
 * @property {string} label
 * @property {boolean} requiresVeiculo
 * @property {string|null} veiculoCategoria - bucket Relatório Veículo (combustivel|manutencao|...)
 * @property {string} chartColor
 * @property {string} statColor - chave MUI StatCard (info|success|warning|default|...)
 */

/** @type {TipoNotaDef[]} */
export const TIPO_NOTA_DEFS = [
  {
    enum: TIPO_NOTA.ABASTECIMENTO,
    kpiKey: 'abastecimento',
    label: 'Abastecimento',
    requiresVeiculo: true,
    veiculoCategoria: 'combustivel',
    chartColor: '#1976d2',
    statColor: 'info',
  },
  {
    enum: TIPO_NOTA.ALIMENTACAO,
    kpiKey: 'alimentacao',
    label: 'Alimentação',
    requiresVeiculo: false,
    veiculoCategoria: null,
    chartColor: '#2e7d32',
    statColor: 'success',
  },
  {
    enum: TIPO_NOTA.CORREIO,
    kpiKey: 'correio',
    label: 'Correio',
    requiresVeiculo: false,
    veiculoCategoria: null,
    chartColor: '#5c6bc0',
    statColor: 'default',
  },
  {
    enum: TIPO_NOTA.HOSPEDAGEM,
    kpiKey: 'hospedagem',
    label: 'Hospedagem',
    requiresVeiculo: false,
    veiculoCategoria: null,
    chartColor: '#7b1fa2',
    statColor: 'default',
  },
  {
    enum: TIPO_NOTA.MANUTENCAO_VEICULO,
    kpiKey: 'manutencaoVeiculo',
    label: 'Manutenção do Veículo',
    requiresVeiculo: true,
    veiculoCategoria: 'manutencao',
    chartColor: '#ed6c02',
    statColor: 'warning',
  },
  {
    enum: TIPO_NOTA.COMPRA_MANUTENCAO_VEICULAR,
    kpiKey: 'compraManutencaoVeicular',
    label: 'Compra Man. Veicular',
    requiresVeiculo: true,
    veiculoCategoria: 'manutencao',
    chartColor: '#f57c00',
    statColor: 'warning',
  },
  {
    enum: TIPO_NOTA.LIMPEZA,
    kpiKey: 'limpezaVeicular',
    label: 'Limpeza Veicular',
    requiresVeiculo: true,
    veiculoCategoria: 'manutencao',
    chartColor: '#00897b',
    statColor: 'info',
  },
  {
    enum: TIPO_NOTA.PEDAGIO,
    kpiKey: 'pedagio',
    label: 'Pedágio/Balça',
    requiresVeiculo: false,
    veiculoCategoria: 'outros',
    chartColor: '#00838f',
    statColor: 'info',
  },
  {
    enum: TIPO_NOTA.OUTROS,
    kpiKey: 'outros',
    label: 'Outros',
    requiresVeiculo: false,
    veiculoCategoria: null,
    chartColor: '#757575',
    statColor: 'default',
  },
];

export const TIPO_NOTA_BY_ENUM = TIPO_NOTA_DEFS.reduce((acc, def) => {
  acc[def.enum] = def;
  return acc;
}, {});

export const TIPO_NOTA_BY_KPI_KEY = TIPO_NOTA_DEFS.reduce((acc, def) => {
  acc[def.kpiKey] = def;
  return acc;
}, {});

/** Options para DefaultSelectV2 / CustomFilterV2 (`{ id, nome }`). */
export const TIPO_NOTA_OPTIONS = TIPO_NOTA_DEFS.map((def) => ({
  id: def.enum,
  nome: def.label,
}));

export const TIPOS_COM_VEICULO = TIPO_NOTA_DEFS
  .filter((def) => def.requiresVeiculo)
  .map((def) => def.enum);

export const requiresVeiculo = (tipo) => TIPOS_COM_VEICULO.includes(tipo);

/** Mapa enum TipoNota → bucket do Relatório Veículo (só tipos de Nota). */
export const TIPO_CATEGORIA_VEICULO_MAP = TIPO_NOTA_DEFS.reduce((acc, def) => {
  if (def.veiculoCategoria) {
    acc[def.enum] = def.veiculoCategoria;
  }
  return acc;
}, {});

export const CATEGORIA_CHART_COLORS = TIPO_NOTA_DEFS.reduce((acc, def) => {
  acc[def.label] = def.chartColor;
  acc[def.enum] = def.chartColor;
  acc[def.kpiKey] = def.chartColor;
  return acc;
}, {});

export const CATEGORIA_STAT_COLORS = TIPO_NOTA_DEFS.reduce((acc, def) => {
  acc[def.label] = def.statColor;
  return acc;
}, {});

export const FILTERABLE_CATEGORIAS = TIPO_NOTA_DEFS.map((d) => d.label);

export const EVOLUCAO_SERIES_KEYS = TIPO_NOTA_DEFS.map(({ kpiKey, label }) => ({
  key: kpiKey,
  label,
}));

const stripAccents = (value) =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/** Aliases históricos de label (UI antiga → enum atual). */
const LABEL_ALIASES = {
  correios: TIPO_NOTA.CORREIO,
  'compra manutencao veicular': TIPO_NOTA.COMPRA_MANUTENCAO_VEICULAR,
};

/**
 * Resolve TipoNota a partir de enum (ABASTECIMENTO), kpiKey (abastecimento) ou label.
 * O backend do BI costuma enviar kpiKey em `categoria` / `distribuicao`.
 */
export const resolveTipoNotaDef = (tipoNota, categoria) => {
  const tryMatch = (raw) => {
    if (raw == null || raw === '') return null;
    const str = String(raw).trim();
    if (!str) return null;

    if (TIPO_NOTA_BY_ENUM[str]) return TIPO_NOTA_BY_ENUM[str];

    const upper = str.toUpperCase();
    if (TIPO_NOTA_BY_ENUM[upper]) return TIPO_NOTA_BY_ENUM[upper];

    if (TIPO_NOTA_BY_KPI_KEY[str]) return TIPO_NOTA_BY_KPI_KEY[str];

    const lower = str.toLowerCase();
    if (TIPO_NOTA_BY_KPI_KEY[lower]) return TIPO_NOTA_BY_KPI_KEY[lower];

    const norm = stripAccents(lower);
    const byLabel = TIPO_NOTA_DEFS.find(
      (d) => stripAccents(d.label.toLowerCase()) === norm,
    );
    if (byLabel) return byLabel;

    const aliasEnum = LABEL_ALIASES[norm];
    if (aliasEnum && TIPO_NOTA_BY_ENUM[aliasEnum]) {
      return TIPO_NOTA_BY_ENUM[aliasEnum];
    }

    return null;
  };

  return (
    tryMatch(tipoNota)
    || tryMatch(categoria)
    || TIPO_NOTA_BY_ENUM.OUTROS
  );
};

export const getTipoNotaLabel = (tipoNota, categoria) => {
  if (tipoNota != null && TIPO_NOTA_LEGACY_LABELS[tipoNota]) {
    return TIPO_NOTA_LEGACY_LABELS[tipoNota];
  }
  return resolveTipoNotaDef(tipoNota, categoria)?.label || 'Outros';
};

export const getCategoriaChartColor = (categoria, tipoNota) => {
  const def = resolveTipoNotaDef(tipoNota, categoria);
  return CATEGORIA_CHART_COLORS[def.kpiKey] || '#757575';
};
